import React from 'react';
import { Alert, Button, Card, Col, Descriptions, Flex, Image, Popconfirm, Row, Space } from 'antd';
import { formatCurrency } from 'utils/number';
import { getOrderPaymentMethodLabel } from '../paymentMethodLabel';

const getLineImage = (line) => {
  const image = line?.variant?.image
    || line?.variant?.images?.[0]
    || line?.product?.thumbnail
    || line?.product?.image
    || line?.product?.images?.[0]
    || null;

  if (!image) return null;
  if (typeof image === 'string') return image;
  return image?.url || null;
};

const OrderDetailDesktop = ({ viewModel }) => {
  const {
    data,
    customer,
    lineItems,
    id,
    navigate,
    setShowOrderPayment,
    setShowOrderHistoryModal,
    setShowOrderNoteModal,
    setIsModalPaymentProofOpen,
    cancelOrder,
    finishOrder,
    createInvoice,
    createPackingList,
    createShippingLabel,
    openQuickStatusEditor,
  } = viewModel;

  const shippingStatusTitle = data.shipping_status?.title || data.shipping_status_id || '-';
  const internalStatusId = data.internal_status?.id || data.internal_status_id || data.internal?.status?.id;
  const internalStatusTitle = data.internal_status?.title
    || data.internal?.status?.title
    || data.store_setting?.order_internal_status_map?.[internalStatusId]?.title
    || internalStatusId
    || '-';
  const paymentMethodLabel = getOrderPaymentMethodLabel(data);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {data.status == 'canceled' && (
            <Alert
              message="Đơn hàng này đã bị huỷ"
              description={`Lý do huỷ: ${data.cancel?.reason || ''}`}
              type="error"
              closable
            />
          )}

          {data.status == 'finalized' && (
            <Alert
              message="Đơn hàng này đã hoàn tất"
              description={`Ghi chú: ${data.finalized?.note || ''}`}
              type="success"
              closable
            />
          )}

          <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 3 }}>
            <Descriptions.Item label="Mã đơn hàng">{data.id}</Descriptions.Item>
            <Descriptions.Item label="Khách hàng">{customer.full_name}</Descriptions.Item>
            <Descriptions.Item label="SDT">{customer.phone}</Descriptions.Item>
            <Descriptions.Item label="Thanh toán" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{data.payment_status?.title}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Phương thức thanh toán" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{paymentMethodLabel}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Vận chuyển" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{shippingStatusTitle}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Nội bộ" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{internalStatusTitle}</div>
            </Descriptions.Item>

            <Descriptions.Item label="Địa chỉ" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{data.shipping_address?.address}</div>
            </Descriptions.Item>
            <Descriptions.Item label="Ghi Chú" span={3}>
              <div style={{ wordBreak: 'break-word' }}>{data.note}</div>
            </Descriptions.Item>
          </Descriptions>

          {data.commission > 0 && (
            <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 3 }}>
              <Descriptions.Item label="CTV" span={3}>{data.collaborator?.full_name || data.ref_code || '-'}</Descriptions.Item>
              <Descriptions.Item label="SĐT CTV" span={3}>{data.collaborator?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Hoa hồng" span={3}>{formatCurrency(data.commission)}</Descriptions.Item>
            </Descriptions>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <Space wrap>
                <Popconfirm
                  title="Huỷ đơn hàng"
                  description="Bạn có chắc chắn muốn huỷ đơn hàng này?"
                  onConfirm={cancelOrder}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger>Huỷ</Button>
                </Popconfirm>

                <Popconfirm
                  title="Hoàn tất"
                  description="Bạn có chắc chắn muốn hoàn tất đơn hàng này?"
                  onConfirm={finishOrder}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button>Hoàn tất</Button>
                </Popconfirm>

                <Button onClick={() => setShowOrderPayment(true)}>Thanh toán</Button>
                <Button onClick={openQuickStatusEditor}>Cập nhật nhanh trạng thái</Button>
                <Button onClick={() => setShowOrderHistoryModal(true)}>Lịch sử</Button>
                <Button onClick={() => setShowOrderNoteModal(true)}>Ghi chú</Button>
              </Space>
            </div>
            <div style={{ flexGrow: 1, flexShrink: 1, minWidth: 0 }}>
              <Flex justify="flex-end">
                <Space align="end" wrap>
                  <Button type="primary" onClick={() => navigate(`/order/edit/${id}`)}>Sửa</Button>
                  <Button type="primary" onClick={createInvoice}>In Hoá Đơn</Button>
                  {data.payment_proof?.image && (
                    <Button type="primary" onClick={() => setIsModalPaymentProofOpen(true)}>Thông tin thanh toán</Button>
                  )}
                  <Button type="primary" onClick={createPackingList}>In Phiếu Đóng Gói</Button>
                  <Button type="primary" onClick={createShippingLabel}>In Tem Vận Chuyển</Button>
                </Space>
              </Flex>
            </div>
          </div>
        </Space>
      </Card>

      <Row gutter={16}>
        <Col span={16}>
          <Card>
            <table className="tbl_order" style={{ width: '100%' }}>
              <tr>
                <th>Sản phẩm</th>
                <th>Số lượng</th>
                <th>Đơn giá</th>
                <th>Thành tiền</th>
              </tr>

              <tbody className="ant-table-tbody">
                {lineItems.map((line, index) => (
                  <tr key={`${line.product?.id || ''}-${line.variant?.id || ''}-${line.id || ''}`}>
                    <td>
                      <Space size={8}>
                        <span style={{ minWidth: 16, fontWeight: 500 }}>{index + 1}</span>
                        {getLineImage(line) && (
                          <Image
                            src={getLineImage(line)}
                            width={36}
                            height={36}
                            style={{ objectFit: 'cover', borderRadius: 6 }}
                            fallback='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzNiIgaGVpZ2h0PSIzNiI+PHJlY3Qgd2lkdGg9IjM2IiBoZWlnaHQ9IjM2IiBmaWxsPSIjZjVmNWY1Ii8+PC9zdmc+'
                          />
                        )}
                        <span>
                          <div>{line.product?.name}</div>
                          {line.variant?.variant_title && (
                            <div style={{ fontSize: 12, color: '#888' }}>{line.variant.variant_title}</div>
                          )}
                        </span>
                      </Space>
                    </td>
                    <td>{line.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(line.price)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(line.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <table style={{ width: '100%' }} className="tbl_order">
              <colgroup>
                <col></col>
                <col style={{ textAlign: 'right' }}></col>
              </colgroup>
              <tbody className="ant-table-tbody">
                <tr>
                  <td>Tiền hàng</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.sub_amount)}</td>
                </tr>
                <tr>
                  <td>Giảm giá</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.discount_amount)}</td>
                </tr>
                <tr>
                  <td>Mã khuyến mãi</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.voucher_amount)}</td>
                </tr>
                <tr>
                  <td>Xu</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.point_discount)}</td>
                </tr>
                <tr>
                  <td>Phí ship</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.shipping_amount)}</td>
                </tr>
                <tr>
                  <td>Tổng tiền</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(data.total_amount)}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default OrderDetailDesktop;
